/**
 * UranIA PA — TanStack Router config (US-009 → US-010).
 *
 * Albero composto per SEGMENTO DI GUARDIA, non più per area tematica —
 * vedi "UranIA PA — Riordino della mappa delle rotte" (2026-09-07),
 * Rilascio A passo A.1. Quattro segmenti pathless (`_pubblico`,
 * `_servizi`, `_amministrazione`, `_dev`) portano ciascuno una guardia
 * unica in `beforeLoad` (`lib/ruoli.ts`); aggiungere una rotta dentro un
 * segmento significa averla già protetta. `/auth/*` resta senza segmento:
 * protocollo OIDC, nessuna guardia, nessuna UI propria.
 *
 * Quando saliremo sopra le 10-12 route per segmento passeremo al code-gen
 * file-based via `@tanstack/router-plugin/vite`; le firme `createRoute`
 * qui sono già compatibili.
 */

import { createRouter } from '@tanstack/react-router';

import { NotFoundPage } from './features/sistema/NotFoundPage.tsx';
import { Route as AccediRoute } from './routes/accedi.tsx';
import { Route as AccessoNegatoRoute } from './routes/accesso-negato.tsx';
import { Route as AdminConsoleRoute } from './routes/admin/index.tsx';
import { Route as AdminPiattaformaRoute } from './routes/admin/piattaforma/index.tsx';
import { Route as AdminComplianceRoute } from './routes/admin/compliance/index.tsx';
import { Route as AdminBancaDatiRoute } from './routes/admin/banca-dati/index.tsx';
import { Route as AdminCorpusRoute } from './routes/admin/corpus/index.tsx';
import { Route as AdminCorpusIngestioneRoute } from './routes/admin/corpus/ingestione/index.tsx';
import { Route as AdminLlmProvidersRoute } from './routes/admin/llm-providers/index.tsx';
import { Route as AdminTipiDocumentoRoute } from './routes/admin/tipi-documento/index.tsx';
import { Route as AdminCutoverGatesRoute } from './routes/admin/cutover-gates/index.tsx';
import { Route as AdminConformitaRoute } from './routes/admin/conformita/index.tsx';
import { Route as AdminConformitaGateRoute } from './routes/admin/conformita/gate/index.tsx';
import { Route as AdminSkillDetailRoute } from './routes/admin/skills/$skillId.tsx';
import { Route as AdminSkillListRoute } from './routes/admin/skills/index.tsx';
import { Route as AdminSkillNewRoute } from './routes/admin/skills/new.tsx';
import { Route as AdminTenantDetailRoute } from './routes/admin/tenants/$tenantId.tsx';
import { Route as AdminTenantListRoute } from './routes/admin/tenants/index.tsx';
import { Route as AmministrazioneSegmentRoute } from './routes/_amministrazione.tsx';
import { Route as AuthCallbackRoute } from './routes/auth.callback.tsx';
import { Route as AuthLogoutRoute } from './routes/auth.logout.tsx';
import { Route as AuthSilentRenewRoute } from './routes/auth.silent-renew.tsx';
import { Route as ConsoleRoute } from './routes/console.tsx';
import { Route as ConsoleIngestionRoute } from './routes/console.ingestion.tsx';
import { Route as CoworkRoute } from './routes/cowork/index.tsx';
import { Route as DashboardRoute } from './routes/dashboard.tsx';
import { devTree } from './dev-routes.ts';
import { Route as IndexRoute } from './routes/index.tsx';
import { Route as LoginRoute } from './routes/login.tsx';
import { Route as LoginBeneficiariRoute } from './routes/login/beneficiari.tsx';
import { Route as LoginPaRoute } from './routes/login/pa.tsx';
import { Route as ManutenzioneRoute } from './routes/manutenzione.tsx';
import { Route as OnboardingRoute } from './routes/onboarding/index.tsx';
import { Route as OnboardingPaRoute } from './routes/onboarding/pa.tsx';
import { Route as OnboardingRiRoute } from './routes/onboarding/ri.tsx';
import { Route as OverviewRoute } from './routes/overview.tsx';
import { Route as PiattaformaRoute } from './routes/piattaforma.tsx';
import { Route as TrasparenzaIaRoute } from './routes/trasparenza-ia.tsx';
import { Route as AccessibilitaRoute } from './routes/accessibilita.tsx';
import { Route as PrivacyRoute } from './routes/privacy.tsx';
import { Route as PubblicoSegmentRoute } from './routes/_pubblico.tsx';
import { Route as RootRoute } from './routes/__root.tsx';
import { Route as ScegliAccessoRoute } from './routes/scegli-accesso.tsx';
import { Route as ServiziRoute } from './routes/servizi.tsx';
import { Route as ServiziSegmentRoute } from './routes/_servizi.tsx';
import { Route as ImmersivoRoute } from './routes/immersivo.tsx';
import { Route as WorkspaceMod5IndexRoute } from './routes/workspace/mod5.index.tsx';
import { Route as WorkspaceMod5Route } from './routes/workspace/mod5.$docId.tsx';

const pubblicoTree = PubblicoSegmentRoute.addChildren([
  IndexRoute,
  OverviewRoute,
  AccediRoute,
  ScegliAccessoRoute,
  LoginRoute,
  LoginPaRoute,
  LoginBeneficiariRoute,
  PiattaformaRoute,
  TrasparenzaIaRoute,
  AccessibilitaRoute,
  PrivacyRoute,
]);

const serviziTree = ServiziSegmentRoute.addChildren([
  DashboardRoute,
  ServiziRoute,
  ImmersivoRoute,
  OnboardingRoute,
  OnboardingPaRoute,
  OnboardingRiRoute,
  WorkspaceMod5IndexRoute,
  WorkspaceMod5Route,
  CoworkRoute,
]);

const amministrazioneTree = AmministrazioneSegmentRoute.addChildren([
  ConsoleRoute,
  ConsoleIngestionRoute,
  AdminConsoleRoute,
  AdminPiattaformaRoute,
  AdminTenantListRoute,
  AdminTenantDetailRoute,
  AdminSkillListRoute,
  AdminSkillNewRoute,
  AdminSkillDetailRoute,
  AdminCorpusRoute,
  AdminCorpusIngestioneRoute,
  AdminBancaDatiRoute,
  AdminTipiDocumentoRoute,
  AdminLlmProvidersRoute,
  AdminConformitaRoute,
  AdminConformitaGateRoute,
  AdminComplianceRoute,
  AdminCutoverGatesRoute,
]);

const routeTree = RootRoute.addChildren([
  pubblicoTree,
  serviziTree,
  amministrazioneTree,
  // Assente nella build di produzione vera (`devTree === null` a runtime,
  // stub sostituito da un hook `resolveId` in `vite.config.ts` prima che
  // Rollup risolva l'import) — presente in dev e in `--mode e2e`. `tsc`
  // vede sempre il modulo reale (gli alias/hook di Vite non esistono per
  // il compilatore), quindi il ternary risulta "sempre vero" solo a
  // livello di tipo: a runtime, con lo stub, non lo è. Vedi `dev-routes.ts`.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  ...(devTree ? [devTree] : []),
  AuthCallbackRoute,
  AuthSilentRenewRoute,
  AuthLogoutRoute,
  AccessoNegatoRoute,
  ManutenzioneRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  // Rotta di sistema "*" (Riordino della mappa delle rotte §3, Rilascio A
  // passo A.2): qualunque URL non risolto dall'albero, non solo quelli
  // dentro un segmento — copre anche typo su /auth/* o rotte fuse.
  defaultNotFoundComponent: NotFoundPage,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
